import React, { useCallback, useEffect, type ComponentType } from 'react';
import { useGetCompassPermissions } from '@atlassian/jira-compass-common/src/services/use-get-compass-permissions/index.tsx';
import { useGlobalComponentsProperty } from '@atlassian/jira-compass-common/src/services/use-global-components-property/main.tsx';
import { useCompassProvisionStatus } from '@atlassian/jira-compass-cross-flow/src/services/license-information/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useFlagService } from '@atlassian/jira-flags';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useGetBootstrapSyncStatus } from '../use-get-bootstrap-sync-status/index.tsx';
import messages from './messages.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GlobalComponentsProps = any & { projectKey: string; refreshComponents: () => void };

export const withGlobalComponentsProperty =
	(WrappedComponent: ComponentType<GlobalComponentsProps>) => (props: GlobalComponentsProps) => {
		const { projectKey, refreshComponents } = props;
		const { enabled, loading, error, setProperty } = useGlobalComponentsProperty(projectKey);
		const { showFlag } = useFlagService();

		const provisionStatus = useCompassProvisionStatus();
		const { startPollingBootstrapSyncStatus, stopPollingBootstrapSyncStatus, isSyncInProgress } =
			useGetBootstrapSyncStatus();
		const { loading: permissionsLoading, hasCompassPermissions } = useGetCompassPermissions();

		useEffect(() => {
			if (!loading && enabled === undefined && error) {
				showFlag({
					type: 'error',
					title: messages.errorFetching,
				});
			}
		}, [showFlag, loading, error, enabled]);

		useEffect(() => {
			if (provisionStatus === 'error') {
				showFlag({
					type: 'error',
					title: messages.errorProvisioning,
				});
			}
		}, [showFlag, provisionStatus]);

		const toggleProperty = useCallback(
			(newValue: boolean) => {
				setProperty(newValue)
					.then(() => {
						if (newValue) {
							startPollingBootstrapSyncStatus();
						} else {
							stopPollingBootstrapSyncStatus();
						}

						refreshComponents();
					})
					.catch(() => {
						showFlag({
							type: 'error',
							title: messages.errorToggling,
						});
					});
			},
			[
				refreshComponents,
				setProperty,
				showFlag,
				startPollingBootstrapSyncStatus,
				stopPollingBootstrapSyncStatus,
			],
		);

		return (
			<>
				{!loading && provisionStatus !== 'loading' && provisionStatus !== 'error' && (
					<FireScreenAnalytics
						attributes={{
							isCompassProvisioned: provisionStatus === 'provisioned',
							globalComponentsEnabled: enabled,
						}}
					/>
				)}
				<JSErrorBoundary
					fallback="unmount"
					packageName="jiraProjectDirectoriesComponents"
					teamName="compass-lodestone"
					id="globalComponentsPropertyHOC"
					sendToPrivacyUnsafeSplunk
				>
					<WrappedComponent
						{...props}
						globalComponentsProperty={{
							enabled,
							hasCompassPermissions,
							loading: loading || isSyncInProgress || permissionsLoading,
							setProperty: toggleProperty,
						}}
					/>
				</JSErrorBoundary>
			</>
		);
	};
